define("ROOM4.CCAProductGallery.CCAProductGallery.DetailsGallery.View", [
  "room4_ccaproductgallery_details_gallery.tpl",
  "ROOM4.CCAProductGallery.CCAProductGallery.SchemaHelper",
  "ROOM4.CCAWPhotoSwipeLoader.CCAWPhotoSwipeLoader",
  "Backbone",
  "Utils",
  "Utilities.ResizeImage",
], function (
  room4_ccaproductgallery_details_gallery_tpl,
  SchemaHelper,
  PhotoSwipeLoader,
  Backbone,
  Utils,
  resizeImage
) {
  "use strict";
  var FULLSCREEN_IMAGE_MAX_WIDTH = 1600;
  var FULLSCREEN_IMAGE_MAX_HEIGHT = 1600;

  /**
   * @typedef {Object} GalleryImageSize
   * @property {number} width
   * @property {number} height
   */

  /**
   * @typedef {Object} GalleryImageInput
   * @property {string} url
   * @property {string} [alt]
   * @property {string} caption
   * @property {boolean} [isHero]
   * @property {GalleryImageSize} [size]
   */

  /**
   * @typedef {Object} GalleryImage
   * @property {number} number
   * @property {string} url
   * @property {string} fullscreenUrl
   * @property {string} alt
   * @property {string} caption
   * @property {boolean} isHero
   * @property {GalleryImageSize|null} size
   */

  /**
   * @typedef {Object} PhotoSwipeImage
   * @property {string} src
   * @property {number} width
   * @property {number} height
   * @property {string} [alt]
   */

  /**
   * @typedef {Object} DetailsGalleryViewOptions
   * @property {{ get: Function }} model
   * @property {Object} container
   */

  /**
   * @typedef {Object} SwiperInstance
   * @property {Function} destroy
   */

  /**
   * @typedef {new (element: Element, options: Object) => SwiperInstance} SwiperConstructor
   */

  /**
   * @typedef {Object} GalleryContext
   * @property {GalleryImage[]} images
   * @property {boolean} hasImages
   * @property {boolean} hasMultipleImages
   */

  return Backbone.View.extend({
    template: room4_ccaproductgallery_details_gallery_tpl,

    events: {
      "click .ccaproductgallery-details-gallery-trigger": "openPhotoSwipe",
    },

    initialize: function initialize(options) {
      options = options || {};

      this.model = options.model;
      this.container = options.container;
      this.canonicalUrl = options.canonicalUrl || "";
      this.on("afterViewRender", this.initGallerySwiper, this);
    },

    initGallerySwiper: function initGallerySwiper() {
      var self = this;
      var deferredSwiper =
        typeof document !== "undefined" && document.deferredSwiper;
      var SwiperConstructor = typeof window !== "undefined" && window.Swiper;

      if (!this.hasGalleryImages()) {
        return;
      }

      if (this.gallerySwiper) {
        return;
      }

      if (SwiperConstructor) {
        this.createGallerySwiper(SwiperConstructor);
        return;
      }

      if (deferredSwiper && deferredSwiper.then) {
        deferredSwiper.then(
          function (LoadedSwiperConstructor) {
            self.createGallerySwiper(LoadedSwiperConstructor);
          },
          function (error) {
            console.error("Unable to load product gallery Swiper:", error);
          }
        );
      }
    },

    createGallerySwiper: function createGallerySwiper(SwiperConstructor) {
      var mainElement = this.$(".ccaproductgallery-details-gallery-main")[0];
      var thumbsElement = this.$(
        ".ccaproductgallery-details-gallery-thumbs"
      )[0];
      var imageCount = this.getGalleryImages().length;
      var thumbsSwiper;

      if (!SwiperConstructor || !mainElement || this.gallerySwiper) {
        return;
      }

      try {
        if (thumbsElement && imageCount > 1) {
          thumbsSwiper = new SwiperConstructor(thumbsElement, {
            slidesPerView: "auto",
            spaceBetween: 16,
            freeMode: true,
            watchSlidesProgress: true,
            observer: true,
            observeParents: true,
            breakpoints: {
              0: {
                slidesPerView: "auto",
                spaceBetween: 8,
              },
              768: {
                slidesPerView: "auto",
                spaceBetween: 16,
              },
            },
          });
        }

        this.galleryThumbsSwiper = thumbsSwiper || null;
        this.gallerySwiper = new SwiperConstructor(mainElement, {
          slidesPerView: 1,
          spaceBetween: 0,
          lazyPreloadPrevNext: 1,
          speed: 300,
          observer: true,
          observeParents: true,
          thumbs: thumbsSwiper
            ? {
                swiper: thumbsSwiper,
              }
            : undefined,
        });
      } catch (error) {
        console.error("Error initializing product gallery Swiper:", error);
      }
    },

    openPhotoSwipe: function openPhotoSwipe(event) {
      var self = this;
      var target = event.currentTarget;
      var index = parseInt(target.getAttribute("data-gallery-index"), 10) || 0;

      event.preventDefault();
      event.stopPropagation();

      if (!PhotoSwipeLoader.canLoad(this.container)) {
        if (target.href) {
          window.location.href = target.href;
        }

        return false;
      }

      PhotoSwipeLoader.load(this.container).then(
        function (PhotoSwipeApi) {
          self.ensurePhotoSwipe(PhotoSwipeApi);

          if (self.photoSwipeLightbox) {
            self.photoSwipeLightbox.loadAndOpen(index);
          }
        },
        function (error) {
          console.error("Unable to load product gallery lightbox:", error);

          if (target.href) {
            window.location.href = target.href;
          }
        }
      );

      return false;
    },

    ensurePhotoSwipe: function ensurePhotoSwipe(PhotoSwipeApi) {
      if (this.photoSwipeLightbox || !PhotoSwipeApi) {
        return;
      }

      this.photoSwipeLightbox = PhotoSwipeApi.createLightbox({
        dataSource: this.getPhotoSwipeImages(),
      });
      this.photoSwipeLightbox.init();
    },

    getPhotoSwipeImages: function getPhotoSwipeImages() {
      var photoSwipeImages = [];

      this.getGalleryImages().forEach(function (image) {
        var fullscreenSize = getContainedImageSize(
          image.size,
          FULLSCREEN_IMAGE_MAX_WIDTH,
          FULLSCREEN_IMAGE_MAX_HEIGHT
        );

        photoSwipeImages.push({
          src: image.fullscreenUrl,
          width: fullscreenSize.width,
          height: fullscreenSize.height,
          alt: image.alt,
        });
      });

      return photoSwipeImages;
    },

    /**
     *
     * @returns {Array<GalleryImage>}
     */
    getGalleryImages: function getGalleryImages() {
      var title = (this.model && this.model.get("title")) || "";
      var vehicle = (this.model && this.model.get("vehicle")) || "";
      var pageUrl =
        this.canonicalUrl ||
        SchemaHelper.getAbsoluteUrl(
          "/gallery/" +
            encodeURIComponent((this.model && this.model.get("slug")) || "")
        );
      var /** @type {Array<GalleryImageInput>} */ images =
          (this.model && this.model.get("images")) || [];
      var galleryImages = /** @type {Array<GalleryImage>} */ ([]);

      images.forEach(function (image) {
        if (!image.url) {
          return;
        }

        var galleryImage = {
          number: galleryImages.length + 1,
          url: image.url,
          fullscreenUrl: resizeImage(image.url, "fullscreen"),
          caption: image.caption,
          alt:
            image.alt || title || vehicle || Utils.translate("Gallery image"),
          isHero: !!image.isHero,
          size: getValidImageSize(image.size),
        };
        var imageSchema = SchemaHelper.getImageSchemaContext(
          galleryImage,
          galleryImages.length,
          pageUrl,
          title || vehicle || Utils.translate("Gallery image"),
          {
            contentUrl: galleryImage.fullscreenUrl,
            useIdForUrl: true,
            publicationDate:
              (this.model && this.model.get("publishdate")) || "",
          }
        );

        galleryImage.schemaId = imageSchema ? imageSchema.id : "";
        galleryImage.schemaDomId = imageSchema ? imageSchema.domId : "";
        galleryImage.schemaUrl = imageSchema ? imageSchema.url : "";
        galleryImage.schemaContentUrl = imageSchema
          ? imageSchema.contentUrl
          : "";
        galleryImage.schemaCaption = imageSchema ? imageSchema.caption : "";
        galleryImage.schemaLicense = imageSchema ? imageSchema.license : "";
        galleryImage.schemaAcquireLicensePage = imageSchema
          ? imageSchema.acquireLicensePage
          : "";
        galleryImage.schemaCreditText = imageSchema
          ? imageSchema.creditText
          : "";
        galleryImage.schemaCopyrightNotice = imageSchema
          ? imageSchema.copyrightNotice
          : "";
        galleryImage.schemaCreatorName = imageSchema
          ? imageSchema.creatorName
          : "";
        galleryImage.hasSchemaCaption = !!(
          imageSchema && imageSchema.hasCaption
        );
        galleryImage.hasSchemaCopyrightNotice = !!(
          imageSchema && imageSchema.hasCopyrightNotice
        );

        galleryImages.push(galleryImage);
      }, this);

      return galleryImages;
    },

    hasGalleryImages: function hasGalleryImages() {
      return !!this.getGalleryImages().length;
    },

    destroy: function destroy() {
      if (this.gallerySwiper) {
        this.gallerySwiper.destroy(true, true);
        this.gallerySwiper = null;
      }

      if (this.galleryThumbsSwiper) {
        this.galleryThumbsSwiper.destroy(true, true);
        this.galleryThumbsSwiper = null;
      }

      if (this.photoSwipeLightbox) {
        this.photoSwipeLightbox.destroy();
        this.photoSwipeLightbox = null;
      }

      Backbone.View.prototype.destroy.call(this);
    },

    getContext: function getContext() {
      var galleryImages = this.getGalleryImages();

      return {
        images: galleryImages,
        hasImages: !!galleryImages.length,
        hasMultipleImages: galleryImages.length > 1,
      };
    },
  });

  /**
   * @param {GalleryImageSize|null|undefined} size
   * @returns {GalleryImageSize|null}
   */
  function getValidImageSize(size) {
    var width = parseInt(size && size.width, 10);
    var height = parseInt(size && size.height, 10);

    if (isNaN(width) || width <= 0 || isNaN(height) || height <= 0) {
      return null;
    }

    return {
      width: width,
      height: height,
    };
  }

  /**
   * @param {GalleryImageSize|null|undefined} size
   * @param {number} maxWidth
   * @param {number} maxHeight
   * @returns {GalleryImageSize}
   */
  function getContainedImageSize(size, maxWidth, maxHeight) {
    var validSize = getValidImageSize(size);
    var scale;

    if (!validSize) {
      return {
        width: maxWidth,
        height: maxHeight,
      };
    }

    scale = Math.min(
      maxWidth / validSize.width,
      maxHeight / validSize.height,
      1
    );
    
    return {
      width: Math.round(validSize.width * scale),
      height: Math.round(validSize.height * scale),
    };
  }
});
